<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import { LucideSearch, LucideNewspaper, LucideCircleX } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import type { SearchResponse } from 'meilisearch'
import {
  EntityMicroCreditItem,
  EntityMicroCreditItemSkeleton
} from '~/components/entity/microcredit'
import type {
  IMicroCredit,
  IMicrocreditCompaniesQueryParams
} from '~/components/entity/microcredit'
import { microcreditsTypes } from '~/data/pages/microcredit/mocks'

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const { locale } = useI18n()
const { search: searchMicrocredits } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_microcredit_companies`)

const props = withDefaults(
  defineProps<{
    queryParams?: IMicrocreditCompaniesQueryParams
    hideSearch?: boolean
  }>(),
  {
    queryParams: () => ({
      per_page: '10',
      q: ''
    }),
    hideSearch: false
  }
)
const hitsPerPage = Number(route.query.per_page || props.queryParams.per_page || 10)
const searchQueryString = ref(String(route.query.q || ''))
const currentPage = ref(Number(route.query.page) || 1)
const debouncedQuery = useDebounce(searchQueryString, 500)
const selectedCategories = ref<string[]>([])
const activeFilter = ref<string[] | undefined>(props.queryParams.filter)

const updateSelectedCategories = (newSelected: string[]) => {
  selectedCategories.value = newSelected
  activeFilter.value = newSelected || undefined
}

// Compute search key
const searchKey = computed(() =>
  [
    'microcredits-search',
    debouncedQuery.value,
    currentPage.value,
    (activeFilter.value || []).join(',')
  ].join('-')
)

// Use useAsyncData for SSR support
const {
  data: result,
  pending: isSearching,
  error: searchError
} = await useAsyncData<SearchResponse<IMicroCredit> | null>(
  searchKey,
  async () => {
    try {
      const filters = activeFilter.value || []

      let filter: string[] | undefined = undefined

      if (filters.length > 0) {
        const orConditions = filters.map((f) => `type = "${f}"`)
        filter = [`(${orConditions.join(' OR ')})`]
      }

      return (await searchMicrocredits(debouncedQuery.value, {
        hitsPerPage,
        sort: ['weight:desc', 'highlight:desc', 'updated_at:desc'],
        page: currentPage.value,
        attributesToHighlight: ['title'],
        attributesToSearchOn: [
          'title.ky',
          'title.ru',
          'official_title.ky',
          'official_title.ru',
          'description.ky',
          'description.ru'
        ],
        ...(filter ? { filter } : {})
      })) as unknown as SearchResponse<IMicroCredit>
    } catch (e) {
      console.error('MeiliSearch fetch error:', e)
      return null
    }
  },
  {
    server: true,
    lazy: true,
    watch: false,
    default: () => null
  }
)

const hasError = computed(() => !!searchError.value)

// Initialize component
const isInitializing = ref(true)

const typedHits = computed(() => {
  return (result.value?.hits || []).map((hit) => ({
    id: hit.id,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    url: hit.url,
    logo: hit.logo,
    license: hit.license,
    license_date: hit.license_date,
    credits_count: hit.credits_count,
    deposits_count: hit.deposits_count,
    highlight: hit.highlight,
    ratings: hit.ratings?.average_rate ?? null
  }))
})

onMounted(() => {
  isInitializing.value = false
})

watch(
  () => route.path,
  (path) => {
    if (isInitializing.value) return
    const lastSegment = path.split('/').filter(Boolean).pop()
    if (!lastSegment) return

    const match = microcreditsTypes.find((item) => item.type === lastSegment)
    if (match) {
      selectedCategories.value = [match.type]
      activeFilter.value = [match.type]
    } else {
      selectedCategories.value = []
      activeFilter.value = undefined
    }
  },
  { immediate: true }
)

watch(
  () => props.queryParams,
  () => {
    if (isInitializing.value) return
    currentPage.value = 1
    searchQueryString.value = props.queryParams?.q || ''
  },
  { deep: true }
)

watch(activeFilter, () => {
  if (isInitializing.value) return
  currentPage.value = 1
})

watch(debouncedQuery, () => {
  if (isInitializing.value) return
  currentPage.value = 1
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      q: debouncedQuery.value,
      page: undefined
    }
  })
})


const changePage = (page: number) => {
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      q: searchQueryString.value,
      page
    }
  })
  currentPage.value = page
}
</script>

<template>
  <div class="space-y-5">
    <div v-if="!hideSearch" class="relative">
      <Input
        v-model="searchQueryString"
        :placeholder="$t('common.search_placeholder')"
        class="h-[52px] pl-14 pr-14"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
      <Button
        v-if="searchQueryString"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        @click="searchQueryString = ''"
      >
        <LucideCircleX />
      </Button>
    </div>
    <div class="flex flex-wrap gap-3">
      <div v-for="type in microcreditsTypes" :key="type.type" class="inline-flex items-center">
        <input
          :id="type.type"
          :checked="selectedCategories.includes(type.type)"
          type="checkbox"
          :value="type.type"
          class="peer hidden"
          @change="
            updateSelectedCategories(
              selectedCategories.includes(type.type)
                ? selectedCategories.filter((c) => c !== type.type)
                : [...selectedCategories, type.type]
            )
          "
        />
        <label
          :for="type.type"
          class="FilterLabel bg-white p-4 text-sm"
        >
          {{ $t(type.title) }}
        </label>
      </div>
    </div>
    <section v-if="!isSearching && !hasError && result" aria-labelledby="microcredits">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="microcredits" tag="h2" class="sr-only">{{
          $t('common.microcredit_companies')
        }}</Heading>
        <template v-for="item in typedHits" :key="item.id">
          <EntityMicroCreditItem :item="item" />
        </template>

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityMicroCreditItemSkeleton :count="hitsPerPage" :hide-organization-details="false" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>
  </div>
</template>
